import store from '@/store/store';
import { auth } from '@/auth/auth';
import { fetchAgreementStatus } from '@/http/requests/agreement';
import { fetchClinics } from '@/http/requests/clinic';
import { getStoredAccessToken } from '@/utils/localStorageHelpers';

/**
 * Route constants
 */
export const ROUTES = {
  ORG_OWNER_CONTRACT_AGREEMENT: '/org-owner/contract-agreement',
  NURSE_CONTRACT_AGREEMENT: '/nurse/contract-agreement',
  HOME: '/',
  S4_APPROVAL_RESPONSE: '/s4-approval-response',
  LOGIN: '/login',
  REGISTER: '/register',
};


/**
 * Routes that should be restricted when all clinics are disabled
 */
export const RESTRICTED_ROUTES = [
  '/org-owner/shop',
  '/org-owner/events',
  '/org-owner/resources',
  '/org-owner/compliance',
  '/basic/shop',
  '/basic/events',
  '/basic/resources',
  '/basic/compliance',
  '/nurse/shop',
  '/nurse/events',
  '/nurse/resources',
  '/nurse/compliance',
  '/doctor/shop',
  '/doctor/events',
  '/doctor/resources',
  '/doctor/compliance',
];

/**
 * Role fallback routes when clinics are disabled
 */
export const ROLE_FALLBACK_ROUTES = {
  NURSE: '/nurse/notice-board',
  DOCTOR: '/doctor/notice-board',
};

/**
 * Insights "home" routes that should redirect to notice-board when swapping workspace.
 * Used so users don't land on an empty insights dashboard after org switch.
 */
export const HOME_INSIGHTS_TO_NOTICE_BOARD = {
  '/org-owner/home': '/org-owner/notice-board',
  '/nurse/home': '/nurse/notice-board',
};

/**
 * If current path is an insights home route, navigate to the corresponding notice-board
 * (full page load). Otherwise reload. Use after workspace/org switch to avoid staying on /home.
 * @param {string} currentPath - Vue Router path (e.g. from this.$route.path)
 */
export function reloadOrRedirectFromHome(currentPath) {
  const noticeBoardPath = HOME_INSIGHTS_TO_NOTICE_BOARD[currentPath];
  if (noticeBoardPath) {
    window.location.href = noticeBoardPath;
  } else {
    location.reload();
  }
}

/**
 * User roles that should fetch notification badges
 */
const NOTIFICATION_BADGE_ROLES = [
  'doctor',
  'nurse',
  'superAdmin',
  'doctorClinic',
  'orgOwner',
  'admin',
];

/**
 * Check if a route is in the except routes list (routes that bypass authentication)
 * @param {string} path - The route path
 * @returns {boolean}
 */
function isExceptRoute(path) {
  if (!path) {
    return false;
  }

  const staticRoutes = ['/register/doctor', '/register/business'];
  if (staticRoutes.includes(path)) {
    return true;
  }

  // Check dynamic routes with token: '/verify-email/:token', '/onboarding/:token'
  // Match by pattern to handle cases where token might be undefined or path is constructed differently
  if (path.startsWith('/verify-email/') || path.startsWith('/onboarding/')) {
    return true;
  }

  return false;
}

/**
 * Check if user should fetch notification badge
 * @param {string} path - Current route path
 * @param {Object} activeUser - Active user from store
 * @returns {boolean}
 */
function shouldFetchNotificationBadge(path, activeUser) {
  return (
    path !== ROUTES.LOGIN &&
    path !== ROUTES.REGISTER &&
    activeUser &&
    NOTIFICATION_BADGE_ROLES.includes(activeUser.userRole)
  );
}

/**
 * Cache key for agreement status
 */
const AGREEMENT_STATUS_CACHE_KEY = '__cachedAgreementStatus';
const AGREEMENT_STATUS_ORG_KEY = '__cachedAgreementOrgId';

/**
 * Get cached agreement status for the current organization
 * @param {string} organizationId - Organization ID
 * @returns {boolean|undefined} - Cached status or undefined if not cached
 */
function getCachedAgreementStatus(organizationId) {
  const cachedOrgId = window[AGREEMENT_STATUS_ORG_KEY];
  // Only use cache if it's for the same organization
  if (cachedOrgId === organizationId && typeof window[AGREEMENT_STATUS_CACHE_KEY] !== 'undefined') {
    return window[AGREEMENT_STATUS_CACHE_KEY];
  }
  return undefined;
}

/**
 * Set cached agreement status
 * @param {string} organizationId - Organization ID
 * @param {boolean} status - Agreement status
 */
function setCachedAgreementStatus(organizationId, status) {
  window[AGREEMENT_STATUS_CACHE_KEY] = status;
  window[AGREEMENT_STATUS_ORG_KEY] = organizationId;
}

/**
 * Handle agreement status guard for AU region
 * @param {Object} to - Target route
 * @param {Function} next - Next function
 * @param {boolean} isOrgOwner - Is organization owner
 * @param {boolean} isNurse - Is nurse
 * @param {string} organizationId - Organization ID
 * @returns {Promise<void>}
 */
export async function handleAgreementStatusGuard(to, next, isOrgOwner, isNurse, organizationId) {
  // The contract page would redirect a migrated org to V2 before the approver sees it.
  if (to.path === ROUTES.S4_APPROVAL_RESPONSE) return next();

  const contractAgreementRoute = isOrgOwner
    ? ROUTES.ORG_OWNER_CONTRACT_AGREEMENT
    : isNurse
      ? ROUTES.NURSE_CONTRACT_AGREEMENT
      : null;

  // Check if we already have cached status for this organization
  const cachedStatus = getCachedAgreementStatus(organizationId);
  
  if (typeof cachedStatus !== 'undefined') {
    // Use cached value
    // null means no agreement check needed (no clinic or error) - allow navigation
    if (cachedStatus === null) {
      return next();
    }
    // false means agreements not accepted - redirect to agreement page
    if (cachedStatus === false && to.path !== contractAgreementRoute) {
      return next(contractAgreementRoute);
    }
    // true means agreements accepted - redirect away from agreement page if on it
    if (cachedStatus === true && to.path === contractAgreementRoute) {
      return next(ROUTES.HOME);
    }
    return next();
  }

  // Fetch agreement status
  try {
    const acceptedStatus = await fetchAgreementStatus(organizationId);
    const onAgreementPage = to.path === contractAgreementRoute;
    setCachedAgreementStatus(organizationId, acceptedStatus);
    
    // null means no agreement check needed (no clinic or error) - allow navigation
    if (acceptedStatus === null && onAgreementPage) {
      return next(ROUTES.HOME);
    }

    // false means agreements not accepted - redirect to agreement page
    if (acceptedStatus === false && !onAgreementPage) {
      return next(contractAgreementRoute);
    }

    // true means agreements accepted - redirect away from agreement page if on it
    if (acceptedStatus === true && onAgreementPage) {
      return next(ROUTES.HOME);
    }
  } catch (error) {
    console.error('Error during agreement status check:', error);
    // On error, allow navigation to proceed
  }

  return next();
}

/**
 * Handle clinic restrictions guard
 * @param {Object} to - Target route
 * @param {Function} next - Next function
 * @param {string} organizationId - Organization ID
 * @param {Object} user - User object
 * @returns {Promise<void>}
 */
export async function handleClinicRestrictionsGuard(to, next, organizationId, user) {
  if (!RESTRICTED_ROUTES.includes(to.path)) {
    return next();
  }

  try {
    const { data: { data: clinics } } = await fetchClinics(organizationId);
    const allDisabled = clinics.length > 0 && clinics.every(c => c.status === 'Disabled');

    if (allDisabled) {
      const fallbackRoute = ROLE_FALLBACK_ROUTES[user.userType.toUpperCase()] || ROUTES.HOME;
      return next(fallbackRoute);
    }
  } catch (error) {
    console.error('Error checking clinic restrictions:', error);
  }

  return next();
}

/**
 * Main route guard handler
 * @param {Object} to - Target route
 * @param {Object} from - Source route
 * @param {Function} next - Next function
 * @returns {Promise<boolean>} - Returns true if should continue, false if redirected
 */
export function handleMainRouteGuard(to, from, next) {
  // Analytics tracking
  if (typeof window['analytics'] !== 'undefined') {
    window['analytics'].page();
  }

  // Allow S4 approval response route regardless of auth status
  if (to.path === ROUTES.S4_APPROVAL_RESPONSE) {
    next();
    return Promise.resolve(false); // Don't continue with other guards
  }

  // Fetch notification badge for specific roles once a JWT is stored
  if (
    shouldFetchNotificationBadge(to.path, store.state.AppActiveUser) &&
    getStoredAccessToken()
  ) {
    store.dispatch('general/fetchNotificationBadge');
  }

  // Handle authentication
  if (!isExceptRoute(to.path)) {
    // @ts-ignore
    auth(to, from, next);
    return Promise.resolve(false); // Auth guard handles next(), don't continue
  }

  return Promise.resolve(true); // Continue with other guards
}

